import React, { createContext, useContext, useState, ReactNode, useEffect, useRef, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { BreadcrumbItem } from "../components/Breadcrumbs/Breadcrumbs";
import { PageStyle } from "../utils/pageStyleResolver";

interface PageMetadataContextType {
  pageMetadata: { title: string; previewText?: string; titleBadge?: string } | null;
  breadcrumbs: BreadcrumbItem[] | null;
  pageStyle: PageStyle | null;
  setPageMetadata: (metadata: { title: string; previewText?: string; titleBadge?: string } | null) => void;
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[] | null) => void;
  setPageStyle: (style: PageStyle | null) => void;
}

const PageMetadataContext = createContext<PageMetadataContextType | undefined>(
  undefined
);

export const PageMetadataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [pageMetadata, setPageMetadata] = useState<{
    title: string;
    previewText?: string;
    titleBadge?: string;
  } | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[] | null>(
    null
  );
  const [pageStyle, setPageStyle] = useState<PageStyle | null>(null);

  return (
    <PageMetadataContext.Provider
      value={{ pageMetadata, breadcrumbs, pageStyle, setPageMetadata, setBreadcrumbs, setPageStyle }}
    >
      {children}
    </PageMetadataContext.Provider>
  );
};

export const usePageMetadataContext = () => {
  const context = useContext(PageMetadataContext);
  if (!context) {
    throw new Error("usePageMetadataContext must be used within PageMetadataProvider");
  }
  return context;
};

interface PageMetadata {
  title: string;
  previewText?: string;
  titleBadge?: string;
  breadcrumbs?: BreadcrumbItem[];
  pageStyle?: PageStyle;
}

/**
 * Simplified hook for setting page metadata (title and breadcrumbs).
 * Automatically handles cleanup on unmount.
 * 
 * @param metadata - Page metadata including title and breadcrumbs
 * @param deps - Optional dependency array for the useEffect (e.g., [portalUid, translations])
 * 
 * @example
 * ```tsx
 * usePageMetadata({
 *   title: "Users",
 *   breadcrumbs: [
 *     { label: "Portal configuration", path: "/portal-configuration" },
 *     { label: "Users" }
 *   ]
 * }, [portalUid, translations]);
 * ```
 */
export const usePageMetadata = (
  metadata: PageMetadata,
  deps: React.DependencyList = []
) => {
  const { setPageMetadata, setBreadcrumbs, setPageStyle } = usePageMetadataContext();
  const location = useLocation();
  const prevBreadcrumbsKeyRef = useRef<string>("");
  const prevTitleRef = useRef<string>("");
  const currentPathRef = useRef<string>(location.pathname);

  // Set metadata immediately and synchronously using useLayoutEffect
  // This ensures metadata is set before the browser paints, preventing flicker
  useLayoutEffect(() => {
    currentPathRef.current = location.pathname;
    
    // Always set metadata immediately on mount/update to ensure it's visible
    setPageMetadata({
      title: metadata.title,
      previewText: metadata.previewText,
      titleBadge: metadata.titleBadge,
    });
    setPageStyle(metadata.pageStyle ?? null);
    prevTitleRef.current = metadata.title;

    // Update breadcrumbs only if content changed (not just reference)
    const currentBreadcrumbsKey = metadata.breadcrumbs
      ? JSON.stringify(metadata.breadcrumbs)
      : "";
    
    if (currentBreadcrumbsKey !== prevBreadcrumbsKeyRef.current) {
      if (metadata.breadcrumbs) {
        setBreadcrumbs(metadata.breadcrumbs);
      } else {
        setBreadcrumbs(null);
      }
      prevBreadcrumbsKeyRef.current = currentBreadcrumbsKey;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    metadata.title,
    metadata.pageStyle?.titleColor,
    metadata.pageStyle?.backgroundColor,
    ...deps,
  ]);

  // Cleanup effect - only clear metadata if route actually changed (not hot reload)
  useEffect(() => {
    const pathWhenMounted = location.pathname;
    
    return () => {
      // Only clear metadata if we're navigating to a different route
      // During hot reload, the path stays the same, so we don't clear
      const timeoutId = setTimeout(() => {
        // Check if route actually changed (not just hot reload)
        // If path changed, it means we navigated away, so clear metadata
        if (currentPathRef.current !== pathWhenMounted) {
          setPageMetadata(null);
          setBreadcrumbs(null);
          setPageStyle(null);
        }
      }, 0);
      
      // Cleanup: clear the timeout if component remounts quickly (hot reload)
      clearTimeout(timeoutId);
    };
  }, [location.pathname, setPageMetadata, setBreadcrumbs, setPageStyle]);
};

